import './styles/main.css';
import { ApiError, api, setUnauthorizedHandler } from './lib/api.js';
import { el, mount } from './lib/dom.js';
import { maybeShowInstallHint } from './lib/install.js';
import { startOutboxLoop } from './lib/outbox.js';
import { current, navigate, on, onNotFound, startRouter } from './lib/router.js';
import { registerServiceWorker } from './lib/sw.js';
import { renderAbout } from './views/about.js';
import { renderHistoryDetail, renderHistoryList } from './views/history.js';
import { renderHome } from './views/home.js';
import { renderLogin } from './views/login.js';
import { renderPrs } from './views/prs.js';
import { renderSession } from './views/session.js';

// Only the positive result is cached: once authenticated, skip re-hitting /me
// on every navigation. A negative result is never cached, so logging in (or a
// session that later returns) is picked up on the next guarded route.
let authed = false;

async function ensureAuth(): Promise<boolean> {
  if (authed) return true;
  try {
    await api.get('/me');
    authed = true;
  } catch {
    authed = false;
  }
  return authed;
}

function showLoading() {
  mount(
    el('main', { class: 'view view--center' }, [
      el('p', { class: 'dim', style: 'text-align:center' }, ['Carregando…']),
    ]),
  );
}

function renderError(retry: () => void) {
  mount(
    el('main', { class: 'view view--center' }, [
      el('div', { class: 'stack stack--md', style: 'text-align:center' }, [
        el('p', { class: 'muted' }, ['Não foi possível carregar.']),
        el('button', { class: 'btn btn--primary', type: 'button', onclick: retry }, [
          'Tentar de novo',
        ]),
      ]),
    ]),
  );
}

async function guard(render: () => void | Promise<void>) {
  showLoading();
  const ok = await ensureAuth();
  if (!ok) {
    navigate('/login');
    return;
  }
  try {
    await render();
  } catch (err) {
    // A 401 already redirected via the unauthorized handler; anything else is
    // a load failure the user can retry.
    if (err instanceof ApiError && err.status === 401) return;
    renderError(() => void guard(render));
  }
}

on('/login', () => {
  if (authed) navigate('/');
  else renderLogin();
});

on('/', () => guard(renderHome));
on('/history', () => guard(renderHistoryList));
on('/history/:id', (params) => guard(() => renderHistoryDetail(params)));
// uPlot is ~22KB and only this view needs it — load it on demand so it stays
// out of the cold-start bundle (home / logging a set never touch the chart).
on('/evolution', () =>
  guard(async () => {
    const { renderEvolution } = await import('./views/evolution.js');
    await renderEvolution();
  }),
);
on('/prs', () => guard(renderPrs));
on('/about', () => guard(renderAbout));
on('/session/:id', (params) => guard(() => renderSession(params)));

onNotFound(() => {
  navigate('/');
});

// A 401 anywhere means the session is gone: drop the cache and bounce to login.
setUnauthorizedHandler(() => {
  authed = false;
  if (current() !== '/login') navigate('/login');
});

startOutboxLoop();
registerServiceWorker();
startRouter();
setTimeout(maybeShowInstallHint, 1500);
